import {
  EntryPlayerStatusPayload,
  EntryStatusPayload,
} from "@bracket/idl/main/entry/model/EntryPt"
import { FightSportPayload } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import type { WeightClassOption } from "@/domain/fight/model/Fight"

/**
 * 신청 폼 입력값.
 * `<input>` · `<select>` 가 다루는 값이므로 전부 문자열이고,
 * 종목 · 성별은 IDL enum 의 **이름 문자열**("AMATEUR_BOXING")을 그대로 담는다.
 */
interface EntryGymInput {
  name: string
  contactName: string
  contactPhoneNo: string
}

interface EntryPlayerInput {
  /** 목록 렌더링 · 오류 매핑용 임시 키 (서버로 보내지 않는다) */
  key: string
  sport: string
  name: string
  birthDate: string
  gender: string
  weightKg: string
  careerYears: string
  competitionCount: string
  /**
   * 신청자가 고른 체급 — 서버가 내려준 선택지를 그대로 담는다
   *
   * 체중으로 추정하지 않는다(대회 당일에 맞춰 온다). 그 부의 체급표가 없으면 null 로 남고
   * 접수 후 운영자가 확정한다.
   */
  weightClass: WeightClassOption | null
}

/** 신청 비밀번호 입력값 — 조회 · 수정 · 취소에 쓰므로 확인 입력을 함께 받는다 */
interface EntryPasswordInput {
  password: string
  passwordConfirm: string
}

/** 문자열 입력 필드 — 체급은 선택지 객체라 별도 setter 를 쓴다 */
type EntryPlayerTextField = Exclude<
  keyof EntryPlayerInput,
  "key" | "weightClass"
>

type EntryGymErrors = Partial<Record<keyof EntryGymInput, string>>
type EntryPlayerErrors = Partial<Record<keyof EntryPlayerInput, string>>
type EntryPasswordErrors = Partial<Record<keyof EntryPasswordInput, string>>

/** 신청 조회 화면 UI 모델 */
interface EntryPlayerSummary {
  id: string
  name: string
  birthDate: string
  genderLabel: string
  sportLabel: string
  sport: FightSportPayload
  weightKg: string
  careerYears: string
  competitionCount: string
  /** 표시용 체중 — 기록을 받지 않는 종목(닭싸움)은 빈 문자열이다 */
  weightText: string
  /** 표시용 경력 · 출전 횟수 — 기록을 받지 않는 종목은 빈 문자열이다 */
  careerText: string
  divisionLabel: string
  weightClassName: string
  status: EntryPlayerStatusPayload
  statusLabel: string
  rejectedReason: string
}

interface EntryDetail {
  id: string
  entryNo: string
  tournamentId: string
  gymName: string
  gymContactName: string
  gymContactPhoneNo: string
  statusLabel: string
  status: EntryStatusPayload
  canceledReason: string
  players: EntryPlayerSummary[]
  tournamentName: string
  tournamentPeriodText: string
}

/** 체육관 본인 조회 목록 한 줄 */
interface EntrySummary {
  id: string
  entryNo: string
  tournamentId: string
  tournamentName: string
  tournamentStartText: string
  gymName: string
  gymContactName: string
  status: EntryStatusPayload
  statusLabel: string
  playerCount: number
  approvedPlayerCount: number
  rejectedPlayerCount: number
  sportLabels: string
  appliedAtText: string
  canceledReason: string
}

/** 목록 한 줄의 선수 심사 배지 — 신청 상태와 섞이지 않게 따로 만든다 */
interface EntryPlayerReviewBadge {
  label: string
  tone: "green" | "amber"
  rejectedText: string
}

interface SelectOption {
  value: string
  label: string
}

/**
 * 신청 상태 라벨 — 체육관이 낸 접수 건 단위다.
 *
 * 건 단위로 갈리는 것은 「접수됐나 · 취소됐나」뿐이라 값이 둘이다.
 * 승인 · 반려는 선수마다 갈려 ENTRY_PLAYER_STATUS_LABELS 가 맡는다.
 */
const ENTRY_STATUS_LABELS: Record<EntryStatusPayload, string> = {
  [EntryStatusPayload.ENTRY_STATUS_UNKNOWN]: "알 수 없음",
  [EntryStatusPayload.ENTRY_RECEIVED]: "접수 완료",
  [EntryStatusPayload.ENTRY_CANCELED]: "신청 취소",
  [EntryStatusPayload.UNRECOGNIZED]: "알 수 없음",
}

/** 선수 상태 라벨 — 선수 한 명 단위의 승인 · 반려다 */
const ENTRY_PLAYER_STATUS_LABELS: Record<EntryPlayerStatusPayload, string> = {
  [EntryPlayerStatusPayload.ENTRY_PLAYER_STATUS_UNKNOWN]: "알 수 없음",
  [EntryPlayerStatusPayload.ENTRY_PLAYER_APPROVAL_WAITING]: "승인 대기",
  [EntryPlayerStatusPayload.ENTRY_PLAYER_APPROVED]: "승인 완료",
  [EntryPlayerStatusPayload.ENTRY_PLAYER_REJECTED]: "반려",
  [EntryPlayerStatusPayload.UNRECOGNIZED]: "알 수 없음",
}

/** 선수 성별 선택지 — GENDER_ANY 는 체급 표기용이라 선수에게 쓰지 않는다 */
const GENDER_OPTIONS: SelectOption[] = [
  { value: "GENDER_MALE", label: "남" },
  { value: "GENDER_FEMALE", label: "여" },
]

/**
 * 관리자 목록의 신청 상태 필터 선택지
 *
 * 신청 상태는 운영자가 바꾸지 않는다 — 접수는 체육관이, 취소도 체육관이 사유와 함께 한다.
 * 그래서 선택지는 「바꿀 상태」가 아니라 「걸러 볼 상태」다.
 */
const ENTRY_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: "ENTRY_RECEIVED", label: "접수 완료" },
  { value: "ENTRY_CANCELED", label: "신청 취소" },
]

/** 관리자 선수 상태 선택지 — 선수 한 명의 승인 · 반려를 바꾼다 */
const ENTRY_PLAYER_STATUS_OPTIONS: SelectOption[] = [
  { value: "ENTRY_PLAYER_APPROVAL_WAITING", label: "승인 대기" },
  { value: "ENTRY_PLAYER_APPROVED", label: "승인 완료" },
  { value: "ENTRY_PLAYER_REJECTED", label: "반려" },
]

/** 휴대폰 번호 (하이픈 유무 모두 허용) */
const PHONE_NO_PATTERN = /^01[0-9]-?\d{3,4}-?\d{4}$/

/** 신청번호 — 상세 조회 · 수정 · 취소의 식별자 */
const ENTRY_NO_PATTERN = /^\d{4}-\d{6}$/

/** 신청 비밀번호 최소 길이 — 서버 검증(6자)과 같은 값이다 */
const MIN_ENTRY_PASSWORD_LENGTH = 6

function entryStatusLabel(status: EntryStatusPayload): string {
  return ENTRY_STATUS_LABELS[status] ?? "알 수 없음"
}

function entryPlayerStatusLabel(status: EntryPlayerStatusPayload): string {
  return ENTRY_PLAYER_STATUS_LABELS[status] ?? "알 수 없음"
}

/**
 * 신청 한 건의 선수 심사 진행 상황
 *
 * 승인은 선수마다 갈리므로 건 단위로는 집계로만 말한다 — 「4/5 승인」처럼 보여주고
 * 반려가 있으면 몇 명인지 따로 내준다(체육관에 연락해야 하는 건이라 목록에서 바로 보인다).
 * 취소된 신청은 심사 대상이 아니라 호출부가 신청 상태로 먼저 가른다.
 */
function entryPlayerReviewBadge(
  summary: EntrySummary,
): EntryPlayerReviewBadge {
  const rejectedText =
    summary.rejectedPlayerCount > 0 ? `${summary.rejectedPlayerCount}명 반려` : ""
  const isAllApproved =
    summary.playerCount > 0 && summary.approvedPlayerCount === summary.playerCount

  if (isAllApproved) {
    return { label: "승인 완료", tone: "green", rejectedText }
  }

  const isNoneApproved = summary.approvedPlayerCount === 0
  if (isNoneApproved) {
    return { label: "승인 대기", tone: "amber", rejectedText }
  }

  return {
    label: `${summary.approvedPlayerCount}/${summary.playerCount} 승인`,
    tone: "amber",
    rejectedText,
  }
}

export {
  ENTRY_NO_PATTERN,
  ENTRY_PLAYER_STATUS_LABELS,
  ENTRY_PLAYER_STATUS_OPTIONS,
  ENTRY_STATUS_FILTER_OPTIONS,
  ENTRY_STATUS_LABELS,
  GENDER_OPTIONS,
  MIN_ENTRY_PASSWORD_LENGTH,
  PHONE_NO_PATTERN,
  entryPlayerReviewBadge,
  entryPlayerStatusLabel,
  entryStatusLabel,
}
export type {
  EntryDetail,
  EntryGymErrors,
  EntryGymInput,
  EntryPasswordErrors,
  EntryPasswordInput,
  EntryPlayerErrors,
  EntryPlayerInput,
  EntryPlayerReviewBadge,
  EntryPlayerTextField,
  EntryPlayerSummary,
  EntrySummary,
  SelectOption,
}
